/* eslint-disable react/jsx-props-no-spreading */
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Controller } from "react-hook-form";
import type { UseFormMethods } from "react-hook-form";
import { format, isSameDay } from "date-fns";
import { TextFieldProps } from "@mui/material";
import { ResponsiveDatePicker } from "@common/ResponsiveDatePicker/ResponsiveDatePicker";
import { IInstallationAppointmentForm } from "./IInstallationAppointmentForm";
import { IAvailableDate } from "./mappers/mapAvailableAppointmentList";
import { LabeledTextField } from "@common/LabeledTextField/LabeledTextField";

interface IParams {
    availableDateList: IAvailableDate[];
    formMethods: UseFormMethods<IInstallationAppointmentForm>;
    fullWidth?: boolean;
    isLoading?: boolean;
    minDate?: Date;
    maxDate?: Date;
}

export const InstallationDatePicker = (props: IParams) => {
    const { availableDateList, formMethods, fullWidth, isLoading, minDate, maxDate } = props;
    const { errors, control, watch, setValue } = formMethods;
    const { t } = useTranslation(["installation", "validation"]);
    const selectedDate = watch("appointmentDate");

    const defaultMinDate = minDate ?? (availableDateList.length > 0 ? availableDateList[0].date : new Date());
    const defaultMaxDate =
        maxDate ??
        (availableDateList.length > 0
            ? availableDateList[availableDateList.length - 1].date
            : new Date(new Date().setFullYear(new Date().getFullYear() + 1)));

    // Select first available timeSlot when date changes
    useEffect(() => {
        if (selectedDate) {
            const selectedDateData = availableDateList.find((availableDate) =>
                isSameDay(availableDate.date, selectedDate)
            );

            if (selectedDateData && selectedDateData.slots.length > 0) {
                const currentTimeSlot = watch("timeSlot");
                const firstSlot = selectedDateData.slots[0];

                // Only set if current slot is not available for this date
                if (!selectedDateData.slots.includes(currentTimeSlot)) {
                    setValue("timeSlot", firstSlot);
                }
            } else {
                setValue("timeSlot", "");
            }
        }
    }, [selectedDate, availableDateList, setValue, watch]);

    // Filter available dates
    const shouldDisableDate = (date: Date | null) => {
        if (!date || isLoading) {
            return false;
        }

        if (availableDateList.length === 0) {
            return true;
        }

        return !availableDateList.some((availableDate) => isSameDay(availableDate.date, date));
    };

    return (
        <Controller
            control={control}
            name="appointmentDate"
            render={(controllerRenderProps) => (
                <ResponsiveDatePicker
                    closeOnSelect
                    inputFormat="dd/MM/yyyy"
                    maxDate={defaultMaxDate}
                    minDate={defaultMinDate}
                    renderInput={(renderProps: TextFieldProps) => (
                        <LabeledTextField
                            {...renderProps}
                            className="xtest_installation_date_input"
                            disabled={isLoading}
                            error={Boolean(errors.appointmentDate)}
                            fullWidth={fullWidth}
                            helperText={errors.appointmentDate?.message}
                            id="appointmentDate"
                            inputRef={controllerRenderProps.ref}
                            isRequired
                            labelText={t("installation:date")}
                            name="appointmentDate"
                            placeholder={format(defaultMinDate, "dd/MM/yyyy")}
                        />
                    )}
                    shouldDisableDate={shouldDisableDate}
                    showToolbar={false}
                    value={controllerRenderProps.value}
                    onChange={controllerRenderProps.onChange}
                />
            )}
            rules={{
                required: t("validation:required"),
            }}
        />
    );
};
